/**
 * React hook for managing end-to-end encryption (EC P-384).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "./redux";
import { selectRouterE2eeEnabled } from "@/features/auth/authSlice";
import {
  initializeEncryption,
  getResolvedKeyId,
  createPublicKeyShareMessage,
  isKeyExchangeMessage,
  parseKeyExchangeMessage,
  handleReceivedPublicKey,
  sendKeySystemCommand,
  ensureFreshPublicKey,
} from "@/services/keyExchangeService";
import {
  getPublicKeySetByUsername,
  getActiveKeySet,
} from "@/services/keyStorageService";
import {
  encryptMessage,
  decryptMessage,
  encryptGroupMessage,
  isEncryptedMessage,
  isEcdhWireFormat,
} from "@/utils/crypto";
import {
  getOrGenerateGroupSymmetricKey,
  distributeGroupKeyToMembers,
  resolveGroupSymmetricKey,
} from "@/services/groupKeyService";
import { GroupMember } from "@/types/users";

let keyAutomationUserId: number | null = null;

export interface EncryptionStatus {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  keyId: string | null;
}

export function useEncryption() {
  const userId = useAppSelector((state) => state.auth.userId);
  const username = useAppSelector((state) => state.auth.username);
  const routerE2eeEnabled = useAppSelector(selectRouterE2eeEnabled);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initAttemptRef = useRef(0);
  const [status, setStatus] = useState<EncryptionStatus>({
    isInitialized: false,
    isLoading: false,
    error: null,
    keyId: null,
  });

  // Initialize encryption when auth context becomes available.
  useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    const init = async () => {
      console.debug(`[E2EE] init() fired — userId=${userId} username=${username}`);
      if (!routerE2eeEnabled) {
        setStatus({ isInitialized: false, isLoading: false, error: null, keyId: null });
        return;
      }
      if (!userId || !username) {
        console.debug(`[E2EE] init() guard: missing userId or username, resetting status`);
        initAttemptRef.current = 0;
        keyAutomationUserId = null;
        setStatus({ isInitialized: false, isLoading: false, error: null, keyId: null });
        return;
      }

      // Fast path: key already resolved in this JS runtime (re-login without page reload).
      // Skip the isLoading state entirely — go straight to isInitialized.
      const cachedKeyId = getResolvedKeyId(Number(userId));
      if (cachedKeyId) {
        console.debug(`[E2EE] fast-path: reusing cached keyId=${cachedKeyId}`);
        initAttemptRef.current = 0;
        setStatus({ isInitialized: true, isLoading: false, error: null, keyId: cachedKeyId });
        return;
      }

      try {
        setStatus((prev) => ({ ...prev, isLoading: true, error: null }));
        console.debug(`[E2EE] calling initializeEncryption(${Number(userId)})`);
        const keyId = await initializeEncryption(Number(userId));
        console.log(`[E2EE] initializeEncryption resolved: keyId=${keyId} → setting isInitialized=true`);
        initAttemptRef.current = 0;
        setStatus({ isInitialized: true, isLoading: false, error: null, keyId });
      } catch (error) {
        console.error("Failed to initialize encryption:", error);
        const nextAttempt = initAttemptRef.current + 1;
        initAttemptRef.current = nextAttempt;
        const maxAttempts = 4;

        setStatus({
          isInitialized: false,
          isLoading: nextAttempt < maxAttempts,
          error:
            nextAttempt >= maxAttempts
              ? error instanceof Error ? error.message : "Unknown error"
              : null,
          keyId: null,
        });

        if (nextAttempt < maxAttempts) {
          retryTimeoutRef.current = setTimeout(init, 1200);
        }
      }
    };

    init();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [userId, username, routerE2eeEnabled]);

  // Auto-share public keys with @keys relay when initialized
  useEffect(() => {
    const automateKeyExchange = async () => {
      if (!status.isInitialized || !status.keyId || !userId || !username) return;

      const uid = Number(userId);
      if (keyAutomationUserId !== uid) keyAutomationUserId = uid;

      try {
        // v2: keys are now shared as SPKI (X509) for Android compatibility.
        // The version suffix forces a one-time re-share so any stale JWK in @keys
        // is replaced with SPKI. Remove the old v1 key to keep sessionStorage tidy.
        const cacheKey = `e2ee:autoShare:v2:lastSharedKeyId:${uid}`;
        sessionStorage.removeItem(`e2ee:autoShare:lastSharedKeyId:${uid}`);
        const lastShared = sessionStorage.getItem(cacheKey);

        if (lastShared !== status.keyId) {
          const cmd = await createPublicKeyShareMessage(uid);
          console.log(`[E2EE] Posting sendkeys: uid=${uid} keyId=${status.keyId}`);
          await sendKeySystemCommand(cmd, uid);
          sessionStorage.setItem(cacheKey, status.keyId);
        } else if (import.meta.env.DEV) {
          console.debug(`[E2EE] Skip auto-share: already shared keyId=${status.keyId}`);
        }
      } catch (error) {
        console.error("Failed to auto-share key (sendkeys):", error);
      }
    };

    automateKeyExchange();
  }, [status.isInitialized, status.keyId, userId, username]);

  /**
   * Encrypts a message for a recipient identified by username.
   * Returns the ECDH wire string: Base64(IV):Base64(ciphertext):Base64(signature)
   */
  const encrypt = async (message: string, recipientUsername: string): Promise<string> => {
    if (!status.isInitialized || !status.keyId || !userId) {
      throw new Error("Encryption not initialized");
    }

    // Refresh the peer's key if our cached copy is stale (peer may have switched devices).
    await ensureFreshPublicKey(recipientUsername, Number(userId));

    const [peerKeys, ownKeySet] = await Promise.all([
      getPublicKeySetByUsername(recipientUsername),
      getActiveKeySet(Number(userId)),
    ]);

    if (!peerKeys) {
      throw new Error(`No public key for user ${recipientUsername}. They need to share their key first.`);
    }
    if (!ownKeySet) {
      throw new Error("No active key set found.");
    }

    return await encryptMessage(
      message,
      peerKeys.encryptPublicKeyJwk,
      ownKeySet.encryptPrivateKeyJwk,
      ownKeySet.signPrivateKeyJwk
    );
  };

  /**
   * Decrypts a received ECDH message.
   * `peerUsername` is the other party's address — required to look up their public key.
   */
  const decrypt = async (encryptedMessage: string, peerUsername: string): Promise<string> => {
    if (!userId) throw new Error("User not authenticated");

    if (!isEcdhWireFormat(encryptedMessage)) {
      return "🔒 [Legacy encrypted message — key not available]";
    }

    const [ownKeySet, peerKeys] = await Promise.all([
      getActiveKeySet(Number(userId)),
      getPublicKeySetByUsername(peerUsername),
    ]);

    if (!ownKeySet) throw new Error("No active key set found.");
    if (!peerKeys) throw new Error(`No public key for ${peerUsername}.`);

    return await decryptMessage(
      encryptedMessage,
      ownKeySet.encryptPrivateKeyJwk,
      peerKeys.encryptPublicKeyJwk
    );
  };

  const generateKeyShareMessage = async (): Promise<string> => {
    if (!userId || !username) throw new Error("User not authenticated");
    return await createPublicKeyShareMessage(Number(userId));
  };

  const processReceivedMessage = async (
    message: string,
    peerUsername?: string
  ): Promise<{
    isKeyExchange: boolean;
    isEncrypted: boolean;
    decryptedMessage?: string;
    originalMessage: string;
  }> => {
    if (isKeyExchangeMessage(message)) {
      const keyMsg = parseKeyExchangeMessage(message);
      if (keyMsg) await handleReceivedPublicKey(keyMsg);
      return { isKeyExchange: true, isEncrypted: false, decryptedMessage: "🔑 Public key received", originalMessage: message };
    }

    if (isEncryptedMessage(message)) {
      try {
        const decryptedMessage = await decrypt(message, peerUsername ?? "");
        return { isKeyExchange: false, isEncrypted: true, decryptedMessage, originalMessage: message };
      } catch (error) {
        console.error("Failed to decrypt message:", error);
        return { isKeyExchange: false, isEncrypted: true, decryptedMessage: "🔒 [Failed to decrypt]", originalMessage: message };
      }
    }

    return { isKeyExchange: false, isEncrypted: false, originalMessage: message };
  };

  /**
   * Checks if encryption is available for a recipient by username.
   * Stable reference (useCallback) so callers can safely include it in useEffect deps.
   */
  const canEncryptFor = useCallback(async (recipientUsername: string): Promise<boolean> => {
    const peerKeys = await getPublicKeySetByUsername(recipientUsername);
    return peerKeys !== null;
  }, []);

  /**
   * Checks if a symmetric encryption key is stored for a group conversation.
   * Stable reference (useCallback) so it can be used in useEffect deps.
   */
  const canEncryptGroup = useCallback(async (groupAddress: string): Promise<boolean> => {
    const key = await resolveGroupSymmetricKey(groupAddress);
    return key !== null;
  }, []);

  /**
   * Encrypts a group message with the group's symmetric key, signed by the sender.
   * Returns the wire string: senderUsername:Base64(IV):Base64(ciphertext):Base64(signature)
   */
  const encryptForGroup = async (message: string, groupAddress: string, groupName: string): Promise<string> => {
    if (!status.isInitialized || !userId || !username) {
      throw new Error("Encryption not initialized");
    }

    const symmetricKeyB64 = await resolveGroupSymmetricKey(groupAddress);
    if (!symmetricKeyB64) {
      throw new Error(`No symmetric key for group "${groupName}". Key has not been distributed yet.`);
    }

    const ownKeySet = await getActiveKeySet(Number(userId));
    if (!ownKeySet) throw new Error("No active key set found.");

    return encryptGroupMessage(message, symmetricKeyB64, ownKeySet.signPrivateKeyJwk);
  };

  /**
   * Ensures the group has a symmetric encryption key, and distributes it to members
   * only when the key is first generated. Only the group owner should call this.
   *
   * Safe (and cheap) to call on every conversation open: once the key exists this is
   * just two IDB reads. It does NOT re-push to every member on each open — members that
   * lack the key (newly added, or peers who re-logged in and lost the old ciphertext)
   * pull it via group_key_request → respondToGroupKeyRequest, and symmetric-key drift
   * self-heals via Path B. Re-broadcasting on every open was redundant SendUserMessage
   * (+ @keys) traffic. Mirrors respondToGroupKeyRequest's `if (!existingKey)` branch.
   * Stable reference (useCallback) so callers can safely include it in useEffect deps.
   */
  const setupGroupEncryption = useCallback(async (
    groupAddress: string,
    groupName: string,
    ownerUsername: string,
    members: GroupMember[]
  ): Promise<void> => {
    if (!status.isInitialized || !userId) return;

    try {
      const alreadyHadKey = (await resolveGroupSymmetricKey(groupAddress)) !== null;
      await getOrGenerateGroupSymmetricKey(groupAddress, groupName, ownerUsername);
      if (!alreadyHadKey) {
        await distributeGroupKeyToMembers(groupAddress, groupName, ownerUsername, members, Number(userId));
      }
    } catch (err) {
      console.error("[GroupKey] setupGroupEncryption failed:", err);
    }
  }, [status.isInitialized, userId]);

  return {
    status,
    encrypt,
    decrypt,
    generateKeyShareMessage,
    processReceivedMessage,
    canEncryptFor,
    canEncryptGroup,
    encryptForGroup,
    setupGroupEncryption,
    isKeyExchangeMessage,
    isEncryptedMessage,
  };
}
